import type { InitConfig, VendorConfig, TrackingConfig, EventTracker, SetupOptions, TrackingPausedReason } from './types';
import { ConfigManager } from './config-manager';
import { EventTrackerImpl } from './event-tracker';
import { PageviewCapture } from './pageview-capture';
import { loadContext, saveContext, clearContext, type PersistedContext } from './persistence';
import { 
  isDebugMode, 
  isDismissed, 
  getStatus, 
  getUserContext, 
  getAccountContext,
  type DebugSDKInstance 
} from './debug-utils';
import { DebugBadge } from './debug-badge';
import { DebugOverlay } from './debug-overlay';

/**
 * Pre-init event queue
 * Captures events before SDK is fully initialized, then replays them after init()
 * This prevents lost events when track() is called before init() completes
 */
interface QueuedCall {
  method: 'track' | 'setup' | 'reset';
  args: any[];
  timestamp: number;
}

const preInitQueue: QueuedCall[] = [];
let isInitialized = false;

// Expose a global shim immediately to capture early events
// This runs at module load time, before init() is called
if (typeof window !== 'undefined' && !(window as any).FirstDistro) {
  (window as any).FirstDistro = {
    // Queue track calls until SDK is ready
    track: (eventName: string, properties?: object) => {
      preInitQueue.push({ method: 'track', args: [eventName, properties], timestamp: Date.now() });
    },
    // Queue setup calls until SDK is ready
    setup: (options: SetupOptions) => {
      preInitQueue.push({ method: 'setup', args: [options], timestamp: Date.now() });
    },
    // Queue reset calls until SDK is ready
    reset: () => {
      preInitQueue.push({ method: 'reset', args: [], timestamp: Date.now() });
    },
    // init() will replace this shim with the real SDK
    init: async (config: InitConfig) => {
      // This will be replaced by the real init() once the class is defined
      // Store config for later
      (window as any)._firstDistroInitConfig = config;
    }
  };
}

/**
 * FirstDistro SDK v2.0
 * 
 * Static API class that supports remote configuration and event tracking.
 * 
 * @example
 * ```typescript
 * // Recommended: initialize with an installation token (fd_...)
 * await FirstDistro.initWithToken('fd_abc123xyz789');
 * FirstDistro.track('event', { prop: 'value' });
 *
 * // Advanced: initialize directly with a publishable browser key (pk_...)
 * await FirstDistro.init({
 *   vendorId: 'vnd_abc123',
 *   apiKey: 'pk_live_xyz789'
 * });
 * FirstDistro.track('event', { prop: 'value' });
 * ```
 */
export class FirstDistro {
  /** @internal Used by React bindings for cleanup — not part of public API */
  static instance: FirstDistro | null = null;
  private configManager: ConfigManager;
  private eventTracker?: EventTracker;
  private currentConfig: VendorConfig | null = null;
  private debug: boolean = false;
  private persistence: boolean = true;
  private autoCapture: boolean = true;  // Auto-capture $pageview on load + SPA route changes
  private captureQueryParams?: string[];  // Allowlist of query params kept on $pageview URLs (default: strip all)
  private maskUrl?: (url: string) => string | null;  // Code-install hook applied after the default query strip
  private pageviewCapture?: PageviewCapture;
  private b2cMode: boolean = false;  // Auto-group users by their ID for B2C vendors
  private anonymousId: string = '';
  private userId?: string;
  private userTraits?: Record<string, any>;
  private accountId?: string;
  private accountTraits?: Record<string, any>;
  
  // Debug mode properties
  private eventCount: number = 0;
  private debugBadge?: DebugBadge;
  private debugOverlay?: DebugOverlay;

  // Tracking paused callback
  private onTrackingPaused?: (reason: TrackingPausedReason) => void;

  private constructor({ vendorId, apiKey, apiUrl, persistence, debug, autoCapture, captureQueryParams, maskUrl, onTrackingPaused }: InitConfig) {
    this.configManager = new ConfigManager({
      vendorId,
      apiKey,
      apiUrl: apiUrl || 'https://firstdistro.com'
    });
    this.debug = debug ?? false;
    this.persistence = persistence ?? true;
    this.autoCapture = autoCapture ?? true;
    this.captureQueryParams = captureQueryParams;
    this.maskUrl = maskUrl;
    this.onTrackingPaused = onTrackingPaused;
  }

  /**
   * Static initialization method
   * Creates singleton instance, fetches config, applies it, and starts polling
   */
  static async init(config: InitConfig): Promise<void> {
    if (typeof window === 'undefined') return; // SSR-safe

    if (FirstDistro.instance) {
      console.warn('[FirstDistro] Already initialized');
      return;
    }

    const sdk = new FirstDistro(config);
    await sdk._initialize();

    // Check if aborted after _initialize() (config fetch) completed
    // This prevents setting instance with stale credentials when token changes
    if (config.signal?.aborted) {
      sdk.destroy();
      throw new DOMException('Aborted', 'AbortError');
    }

    FirstDistro.instance = sdk;
    (window as any).firstDistro = sdk;
    isInitialized = true;
    
    if (sdk.debug) {
      sdk.log('Init', {
        vendorId: config.vendorId,
        persistence: sdk.persistence,
        debug: sdk.debug
      });
    }

    // Replay any events that were queued before init() completed
    if (preInitQueue.length > 0) {
      if (sdk.debug) {
        sdk.log(`Replaying ${preInitQueue.length} queued events`);
      }
      
      for (const { method, args } of preInitQueue) {
        try {
          switch (method) {
            case 'track':
              FirstDistro.track(args[0], args[1]);
              break;
            case 'setup':
              FirstDistro.setup(args[0]);
              break;
            case 'reset':
              FirstDistro.reset();
              break;
          }
        } catch (error) {
          if (sdk.debug) {
            sdk.log(`Failed to replay queued ${method}:`, error);
          }
        }
      }
      
      // Clear the queue
      preInitQueue.length = 0;
    }
  }

  /**
   * Static initialization method using an installation token
   * Fetches credentials from the API and calls init()
   *
   * @param token - Installation token (starts with 'fd_')
   * @param options - Optional configuration overrides
   *
   * @example
   * ```typescript
   * await FirstDistro.initWithToken('fd_abc123xyz789')
   * ```
   */
  static async initWithToken(
    token: string,
    options: Omit<InitConfig, 'vendorId' | 'apiKey'> & { signal?: AbortSignal } = {}
  ): Promise<void> {
    if (typeof window === 'undefined') return; // SSR-safe

    if (FirstDistro.instance) {
      console.warn('[FirstDistro] Already initialized');
      return;
    }

    // Validate token format
    if (!token || !token.startsWith('fd_') || token.length < 10) {
      throw new Error('[FirstDistro] Invalid token format. Token must start with "fd_" and be at least 10 characters.');
    }

    // Determine API URL (default to production)
    const apiUrl = options.apiUrl || 'https://firstdistro.com';

    // Fetch credentials from token endpoint
    // Pass signal to allow cancellation when token changes
    const response = await fetch(`${apiUrl}/api/sdk/token/${token}`, {
      signal: options.signal,
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('[FirstDistro] Invalid or expired token');
      }
      if (response.status === 429) {
        throw new Error('[FirstDistro] Rate limit exceeded. Please try again later.');
      }
      throw new Error(`[FirstDistro] Failed to initialize: ${response.status}`);
    }

    const { vendorId, apiKey } = await response.json();

    if (!vendorId || !apiKey) {
      throw new Error('[FirstDistro] Invalid response from token endpoint');
    }

    // Check if aborted after fetch completed but before init
    // This prevents race condition when token changes during fetch
    if (options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    // Initialize with fetched credentials
    // Spread options first so explicit values (vendorId, apiKey, apiUrl) take precedence
    await FirstDistro.init({
      ...options,
      vendorId,
      apiKey,
      apiUrl,
    });
  }

  /**
   * Private initialization method
   * Fetches config, applies it, and starts polling for changes
   */
  private async _initialize(): Promise<void> {
    // 1. Load persisted context (if persistence enabled)
    if (this.persistence) {
      const context = loadContext();
      if (context) {
        this.anonymousId = context.anonymousId;
        this.userId = context.userId;
        this.userTraits = context.userTraits;
        this.accountId = context.accountId;
        this.accountTraits = context.accountTraits;
        this.log('Context loaded from localStorage', {
          userId: this.userId,
          accountId: this.accountId,
          anonymousId: this.anonymousId
        });
      } else {
        // Generate new anonymous ID
        this.anonymousId = this.generateAnonymousId();
        // Save initial context with anonymous ID
        this.saveContext();
        this.log('Generated new anonymous ID:', this.anonymousId);
      }
    } else {
      // Generate anonymous ID but don't persist
      this.anonymousId = this.generateAnonymousId();
      this.log('Generated anonymous ID (persistence disabled):', this.anonymousId);
    }

    // 2. Fetch config (with cache fallback)
    const config = await this.configManager.fetchConfig();

    // 3. Apply config (enable/disable features)
    this.applyConfig(config);

    // 4. Restore user/account IDs to EventTracker if enabled
    if (this.eventTracker && this.userId) {
      // Restore user context
      (this.eventTracker as any).userId = this.userId;
    }
    if (this.eventTracker && this.accountId) {
      // Restore account context
      (this.eventTracker as any).accountId = this.accountId;
    }

    // Config updates now handled by React Query's refetchOnWindowFocus
    // No polling needed - SDK uses ETag-based conditional requests (HTTP 304)
    
    // 5. Initialize debug mode if enabled
    this.initDebugMode();
  }
  
  /**
   * Initialize debug mode (badge + overlay)
   * Only renders if ?fd_debug=true is in URL and not dismissed
   */
  private initDebugMode(): void {
    if (!isDebugMode() || isDismissed()) return;
    
    // Create overlay first (badge opens it on click)
    this.debugOverlay = new DebugOverlay({
      onClose: () => {
        // Overlay closed, badge still visible
      },
      onDismiss: () => {
        // Permanently dismissed
        this.destroyDebugMode();
      },
    });
    this.debugOverlay.render();
    
    // Update overlay with current state
    this.updateDebugOverlay();
    
    // Create badge (shows status + count)
    this.debugBadge = new DebugBadge({
      onBadgeClick: () => {
        // Toggle overlay on badge click
        if (this.debugOverlay) {
          this.debugOverlay.toggle();
        }
      },
    });
    this.debugBadge.render();
    
    // Update badge with current state
    const status = getStatus(this.getDebugInstance());
    this.debugBadge.update(this.eventCount, status);
  }
  
  /**
   * Get instance data for debug utilities
   */
  private getDebugInstance(): DebugSDKInstance {
    return {
      userId: this.userId,
      userTraits: this.userTraits,
      accountId: this.accountId,
      accountTraits: this.accountTraits,
      eventTracker: this.eventTracker,
      currentConfig: this.currentConfig,
      configManager: this.configManager,
    };
  }
  
  /**
   * Update debug overlay with current state
   */
  private updateDebugOverlay(): void {
    if (!this.debugOverlay) return;
    
    const instance = this.getDebugInstance();
    this.debugOverlay.update({
      status: getStatus(instance),
      userContext: getUserContext(instance),
      accountContext: getAccountContext(instance),
      eventCount: this.eventCount,
    });
  }
  
  /**
   * Update debug badge with current state
   */
  private updateDebugBadge(): void {
    if (!this.debugBadge) return;
    
    const instance = this.getDebugInstance();
    this.debugBadge.update(this.eventCount, getStatus(instance));
  }
  
  /**
   * Destroy debug mode UI
   */
  private destroyDebugMode(): void {
    if (this.debugBadge) {
      this.debugBadge.destroy();
      this.debugBadge = undefined;
    }
    if (this.debugOverlay) {
      this.debugOverlay.destroy();
      this.debugOverlay = undefined;
    }
  }

  /**
   * Generate anonymous ID for user
   */
  private generateAnonymousId(): string {
    return `anon_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Save current context to localStorage (if persistence enabled)
   */
  private saveContext(): void {
    if (!this.persistence) return;

    const context: PersistedContext = {
      anonymousId: this.anonymousId,
      userId: this.userId,
      userTraits: this.userTraits,
      accountId: this.accountId,
      accountTraits: this.accountTraits,
    };

    saveContext(context);
  }

  /**
   * Check if configuration has changed
   * Prevents unnecessary re-initialization
   */
  private hasConfigChanged(newConfig: VendorConfig): boolean {
    if (!this.currentConfig) return true;

    const current = this.currentConfig;

    // Compare feature toggles
    if (newConfig.features.tracking.enabled !== current.features.tracking.enabled) {
      return true;
    }

    // Deep compare config objects
    if (JSON.stringify(newConfig.features.tracking) !== JSON.stringify(current.features.tracking)) {
      return true;
    }

    return false;
  }

  /**
   * Apply configuration (enable/disable features)
   */
  private applyConfig(config: VendorConfig) {
    // Store B2C mode from config
    this.b2cMode = config.features.b2cMode ?? false;
    
    if (this.b2cMode) {
      this.log('B2C mode enabled - setup() will auto-group users by their ID');
    }

    // Event tracking
    if (config.features.tracking.enabled && !this.eventTracker) {
      this.enableEventTracking(config.features.tracking);
    } else if (!config.features.tracking.enabled && this.eventTracker) {
      this.disableEventTracking();
    }

    this.currentConfig = config; // Store for comparison
  }

  /**
   * Enable event tracking (lazy load)
   */
  private enableEventTracking(config: TrackingConfig) {
    this.log('✅ Enabling event tracking');
    this.eventTracker = new EventTrackerImpl({
      config,
      vendorId: this.configManager.getVendorId(),
      apiKey: this.configManager.getApiKey(),
      apiUrl: this.configManager.getApiUrl(),
      debug: this.debug,
      onTrackingPaused: this.onTrackingPaused,
    });

    if (this.autoCapture) {
      this.startPageviewCapture();
    }
  }

  /**
   * Disable event tracking
   */
  private disableEventTracking() {
    this.log('❌ Disabling event tracking');
    this.pageviewCapture?.stop();
    this.pageviewCapture = undefined;
    this.eventTracker?.flush();
    this.eventTracker?.destroy();
    this.eventTracker = undefined;
  }

  /**
   * Start automatic $pageview capture (load + SPA route changes).
   * Identity-gated: only fires once identity is set (a userId or accountId).
   * The account is derived server-side from the user, so a userId alone is
   * enough to attribute the page view (email-only B2B install).
   */
  private startPageviewCapture() {
    if (typeof window === 'undefined') return;
    if (this.pageviewCapture) return;

    this.pageviewCapture = new PageviewCapture({
      hasIdentity: () => Boolean(this.userId || this.accountId),
      onCapture: (properties) => {
        this.log('Auto-capture $pageview:', properties.pathname);
        this.track('$pageview', properties);
      },
      allowedQueryParams: this.captureQueryParams,
      maskUrl: this.maskUrl,
    });
    this.pageviewCapture.start();

    // Capture the initial load if identity is already available
    // (e.g. persisted context restored on init).
    this.pageviewCapture.maybeCapture();
  }

  /**
   * Static method to track an event
   */
  static track(eventName: string, properties?: object): void {
    if (typeof window === 'undefined') return; // SSR-safe
    FirstDistro.instance?.track(eventName, properties);
  }

  /**
   * Static method to reset user/account context
   */
  static reset(): void {
    if (typeof window === 'undefined') return; // SSR-safe
    FirstDistro.instance?.reset();
  }

  /**
   * Static method to set user and account context in one call
   * Handles correct ordering internally (account before user)
   * 
   * @example
   * ```typescript
   * FirstDistro.setup({
   *   user: { id: 'user-123', name: 'John', email: 'john@acme.com' },
   *   account: { id: 'acme-corp', name: 'Acme Corp', plan: 'pro' }
   * });
   * ```
   */
  static setup(options: SetupOptions): void {
    if (typeof window === 'undefined') return; // SSR-safe
    FirstDistro.instance?._setup(options);
  }

  /**
   * Instance method to track an event (called by static method)
   */
  private track(eventName: string, properties?: object): void {
    if (!this.eventTracker) {
      console.warn('[FirstDistro] Tracking not enabled. Enable from dashboard.');
      return;
    }
    this.log('Track:', eventName, properties);
    this.eventTracker.track(eventName, properties);
    
    // Update debug mode event count
    this.eventCount++;
    if (this.debugBadge) {
      this.debugBadge.updateCount(this.eventCount);
    }
    if (this.debugOverlay) {
      this.debugOverlay.updateEventCount(this.eventCount);
    }
  }

  /**
   * Internal method to set user context.
   * The single `$setup` event is emitted by `_setup` after context is set.
   */
  private setUserContext(userId: string, traits?: object): void {
    this.userId = userId;
    this.userTraits = traits as Record<string, any> | undefined;

    this.log('SetUser:', userId, traits);

    // B2C mode: Auto-group user as their own account
    if (this.b2cMode && !this.accountId) {
      this.log('B2C mode: Auto-grouping user as account');
      this.setAccountContext(userId, traits);
    }

    // Save context if persistence enabled
    if (this.persistence) {
      this.saveContext();
      this.log('Context saved to localStorage');
    }

    // Update debug overlay with new user context
    this.updateDebugOverlay();
  }

  /**
   * Internal method to set account context.
   * The single `$setup` event is emitted by `_setup` after context is set.
   */
  private setAccountContext(accountId: string, traits?: object): void {
    this.accountId = accountId;
    this.accountTraits = traits as Record<string, any> | undefined;

    this.log('SetAccount:', accountId, traits);

    // Save context if persistence enabled
    if (this.persistence) {
      this.saveContext();
      this.log('Context saved to localStorage');
    }

    // Update debug overlay with new account context
    this.updateDebugOverlay();
  }

  /**
   * Instance method to reset user/account context (called by static method)
   */
  private reset(): void {
    this.log('Reset: Flushing events and clearing context');
    
    // Flush events before clearing context
    if (this.eventTracker) {
      this.eventTracker.flush();
    }
    
    // Clear user/account context
    this.userId = undefined;
    this.userTraits = undefined;
    this.accountId = undefined;
    this.accountTraits = undefined;
    
    // Reset event count (session count, not persisted)
    this.eventCount = 0;
    
    // Generate new anonymous ID
    this.anonymousId = this.generateAnonymousId();
    
    // Clear persisted context if persistence enabled
    if (this.persistence) {
      clearContext();
      // Save new context with anonymous ID only
      this.saveContext();
      this.log('Context cleared from localStorage');
    }
    
    // Clear EventTracker context
    if (this.eventTracker) {
      (this.eventTracker as any).userId = null;
      (this.eventTracker as any).accountId = null;
    }

    // Clear pageview dedupe state; capture stays paused until next setup()
    // re-establishes identity (accountId is now undefined).
    this.pageviewCapture?.resetState();
    
    // Update debug UI
    this.updateDebugBadge();
    this.updateDebugOverlay();
    
    this.log('Reset complete');
  }

  /**
   * Instance method to set user and account context (called by the static setup()).
   * Handles correct ordering: account first, then user, then emits one `$setup`.
   */
  private _setup(options: SetupOptions): void {
    this.log('Setup:', options);

    // Set account context FIRST (correct order). A single `$setup` event is
    // emitted below.
    if (options.account) {
      const { id, ...traits } = options.account;
      this.setAccountContext(id, Object.keys(traits).length > 0 ? traits : undefined);
    }

    // Then set user context (now linked to account).
    if (options.user) {
      const { id, ...traits } = options.user;
      this.setUserContext(id, Object.keys(traits).length > 0 ? traits : undefined);
    }

    // Emit one unified `$setup` using post-context instance state (includes any
    // b2c-derived accountId). Identity rides the event columns; traits go in
    // properties (Option A keys).
    this.eventTracker?.setup({
      userId: this.userId,
      userTraits: this.userTraits,
      accountId: this.accountId,
      accountTraits: this.accountTraits,
    });

    // Identity is now available — capture the first $pageview post-login.
    this.pageviewCapture?.maybeCapture();
  }

  /**
   * Check if event tracking is enabled
   * Useful for testing and debugging
   */
  isTrackingEnabled(): boolean {
    return this.eventTracker !== undefined;
  }

  /**
   * Get the event tracker instance (for testing/debugging)
   * Returns undefined if tracking is not enabled
   */
  getEventTracker(): EventTracker | undefined {
    return this.eventTracker;
  }

  /**
   * Destroy the SDK instance
   * Cleans up all features
   */
  destroy() {
    this.log('Destroying SDK instance');
    this.destroyDebugMode();
    this.disableEventTracking();
    // Only null the static instance if this instance is the current one
    // This prevents a stale/aborted init from wiping out a valid concurrent instance
    if (FirstDistro.instance === this) {
      FirstDistro.instance = null;
    }
  }

/**
   * Debug logging method
   * Only logs if debug mode is enabled
   */
  private log(...args: any[]): void {
    if (this.debug) {
      console.log('[FirstDistro]', ...args);
    }
  }
}

// Expose to window
// Static FirstDistro class (v2.0) - primary export
if (typeof window !== 'undefined') {
  (window as any).FirstDistro = FirstDistro;
}







